import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { ErrorModal } from "../../components/ModalAlert";
import CheckManager from "./checkManager";
import { amountFromKeystrokes, formatNumberManual } from "../../utils/formatters";
import { FormCard } from "../../components/ui/form";
import { Num } from "../../components/ui";
import { IconArrowRight } from "../../components/ui/icons";
import { tokens } from "../../theme";

/**
 * Sólo define el ORDEN EN PANTALLA. El id de cada forma de pago sale del orden
 * de las claves del estado, que no se toca: por eso se puede reordenar esta
 * lista sin romper nada.
 *
 * "Usar Saldo del Cliente" va último: no es una forma de pago que se cobre,
 * sino lo que queda por aplicar una vez cargado el resto.
 */
const METHODS = [
  { id: "efectivo", label: "Efectivo" },
  { id: "transferencia", label: "Transferencia" },
  { id: "notaCredito", label: "Nota de Crédito" },
  { id: "iibb", label: "Retención IIBB" },
  { id: "retencionIVA", label: "Retención IVA" },
  { id: "retencionGanancia", label: "Retención Ganancia" },
  { id: "saldoFavor", label: "Usar Saldo del Cliente" },
];

/** El estado guarda el importe crudo ("173056.48"); en pantalla va formateado. */
const displayAmount = (value) =>
  value === '' || value === undefined || value === null ? '' : formatNumberManual(value);

/** El cursor siempre al final: los dígitos entran por la derecha. */
const caretToEnd = (input) => {
  if (!input) return;
  const end = input.value.length;
  input.setSelectionRange(end, end);
};

const PaymentsMethods = ({
  setTotalPaymentMethods,
  totalBalance,
  setUsedBalance,
  setPaymentMethods,
  setChecks,
  totalExpensesWithIva,
  totalPaymentMethods,
}) => {
  const [paymentMethods, setPaymentMethodsState] = useState({
    saldoFavor: '',
    efectivo: '',
    transferencia: '',
    notaCredito: '',
    iibb: '',
    retencionIVA: '',
    retencionGanancia: '',
    cheques: ''
  });

  /*
    Lo que falta cubrir si el saldo se cierra desde ESTE campo: se descuenta lo
    que ya cargaron los demás, pero no el propio valor, que va a ser pisado.
    Es exactamente el número que inserta el botón de al lado, así que lo que
    se ve y lo que se copia nunca discrepan.
  */
  const faltaDesde = (id) => {
    const otros = Number(totalPaymentMethods || 0) - parseFloat(paymentMethods[id] || 0);
    return Math.max(0, Number(totalExpensesWithIva || 0) - otros);
  };

  /** Lo que falta en total, para el subtítulo de la tarjeta. */
  const pendiente = Math.max(
    0,
    Number(totalExpensesWithIva || 0) - Number(totalPaymentMethods || 0)
  );

  /** El botón "→" vuelca en el campo el importe que cierra el saldo. */
  const completarCampo = (id) => {
    handleInputChange({ target: { id, value: faltaDesde(id).toFixed(2) } });
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (!/^\d*\.?\d*$/.test(value) && value !== '') {
      e.target.value = '';
      return;
    }

    setPaymentMethodsState(prevState => ({
      ...prevState,
      [id]: value
    }));

    // Asegurarse de que los valores vacíos se traten como 0 para los cálculos
    const updatedPaymentMethods = {
      ...paymentMethods,
      [id]: value === '' ? '0' : value
    };

    const total = Object.values(updatedPaymentMethods)
      .filter(key => key !== 'checkNumber' && key !== 'checkBank' && key !== 'checkEmitionDate' && key !== 'checkIssuerName' && key !== 'checkIssuerCuit' && key !== 'checkTransferredName' && key !== 'checkTransferredCuit' && key !== 'checkState' && key !== 'checkNotes')
      .reduce((acc, val) => {
        return acc + parseFloat(val || 0);
      }, 0);

    setTotalPaymentMethods(total);
    setPaymentMethods({ ...paymentMethods, [id]: value });

    if (id === "saldoFavor") {
      if (parseFloat(value || 0) <= totalBalance) {
        setUsedBalance(parseFloat(value || 0));
      } else {
        ErrorModal({
          title: "Atencion!",
          text: "El importe ingresado en Saldo del Cliente, no puede superar el Saldo disponible.",
        });
        return;
      }
    }
  };

  /*
    Tecleo enmascarado: los dígitos entran por la derecha, así que se llenan
    primero los centavos ("1" -> 0,01; "17305648" -> 173.056,48).
  */
  const handleAmountKeystroke = (e) => {
    const input = e.target;
    const { id } = input;

    handleInputChange({ target: { id, value: amountFromKeystrokes(input.value) } });

    // Después del re-render, para que el cursor no quede en medio del importe.
    requestAnimationFrame(() => caretToEnd(input));
  };

  /*
    Se selecciona todo en vez de vaciar: si tecleás, el primer dígito reemplaza
    igual que antes; si sólo entrás o pasás con Tab, el importe queda.
  */
  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <FormCard
      title="Formas de Pago"
      subtitle={
        pendiente > 0
          ? `Falta cubrir $ ${formatNumberManual(pendiente)}`
          : "El pago cubre el total seleccionado"
      }
      sx={{ width: "100%", minWidth: 0 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {METHODS.map(({ id, label }) => {
          const falta = faltaDesde(id);
          return (
            <Box
              key={id}
              sx={{
                display: "flex",
                // En teléfono la etiqueta va arriba; desde md, todo en una fila.
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: { xs: "5px", md: "12px" },
                minWidth: 0,
              }}
            >
              <Typography
                component="label"
                htmlFor={id}
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: tokens.ink2,
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                {label}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                {/* Lo que falta para cerrar el saldo desde este campo. */}
                <Num
                  sx={{
                    fontSize: "12.5px",
                    color: falta > 0 ? tokens.ink2 : tokens.ink4,
                    whiteSpace: "nowrap",
                    textAlign: "right",
                    minWidth: { xs: 0, md: "96px" },
                  }}
                  title={falta > 0 ? `Falta ${formatNumberManual(falta)}` : "Saldo cubierto"}
                >
                  {formatNumberManual(falta)}
                </Num>

                <Box
                  component="button"
                  type="button"
                  onClick={() => completarCampo(id)}
                  disabled={falta <= 0}
                  aria-label={`Usar ${formatNumberManual(falta)} en ${label}`}
                  title="Copiar este importe al campo"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    padding: 0,
                    borderRadius: `${tokens.radiusControl}px`,
                    border: `1px solid ${tokens.border}`,
                    backgroundColor: tokens.surface,
                    color: tokens.ink2,
                    cursor: "pointer",
                    transition: "border-color 120ms ease, color 120ms ease, background-color 120ms ease",
                    "&:hover:not(:disabled)": {
                      borderColor: tokens.brandBorder,
                      backgroundColor: tokens.brandTint,
                      color: tokens.brandDarker,
                    },
                    "&:disabled": {
                      color: tokens.ink4,
                      cursor: "not-allowed",
                      opacity: 0.55,
                    },
                  }}
                >
                  <IconArrowRight size={15} />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    width: { xs: "100%", md: "170px" },
                    flexGrow: { xs: 1, md: 0 },
                    minWidth: 0,
                    borderRadius: `${tokens.radiusControl}px`,
                    border: `1px solid ${tokens.border}`,
                    backgroundColor: tokens.surface,
                    paddingLeft: "11px",
                    transition: "border-color 120ms ease, box-shadow 120ms ease",
                    "&:hover": { borderColor: tokens.borderStrong },
                    "&:focus-within": {
                      borderColor: tokens.brand,
                      boxShadow: `0 0 0 3px ${tokens.brandTint}`,
                    },
                  }}
                >
                  <Typography sx={{ fontSize: "13px", color: tokens.ink3, flexShrink: 0 }}>$</Typography>
                  <Box
                    component="input"
                    type="text"
                    inputMode="numeric"
                    id={id}
                    value={displayAmount(paymentMethods[id])}
                    placeholder="0,00"
                    onChange={handleAmountKeystroke}
                    onFocus={handleFocus}
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      color: tokens.ink,
                      // 16px evita que iOS haga zoom al enfocar el campo.
                      fontSize: "16px",
                      fontVariantNumeric: "tabular-nums",
                      textAlign: "right",
                      padding: "10px 11px 10px 0",
                      minHeight: 44,
                      "&::placeholder": { color: tokens.ink4 },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <CheckManager
        setPaymentMethods={setPaymentMethods}
        handleInputChange={handleInputChange}
        setChecks={setChecks}
      />
    </FormCard>
  );
};

export default PaymentsMethods;
